import React, { useEffect, useCallback } from "react";
import {
  Col,
  Row,
  Button,
  Table,
  message,
  Space,
  Drawer,
  Popconfirm,
  Tag,
  Typography,
} from "antd";

import { Layout } from "antd";
import { TablePaginationConfig } from "antd/lib/table";
import { useNavigate, useSearchParams } from "react-router-dom";
import { WizardService } from "../../services/WizardService";
import { PageHeader } from "../../wizard/components/PageHeader";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  DropdownAction,
  DefaultColumnBuilder as builder,
} from "../components/ColumnBuilder";
import { Mapping } from "../../types/mapping";
import { MappingForm } from "./MappingForm";
import { AppRoutes } from "../../AppRoutes";
const { Content } = Layout;
const { Text } = Typography;

export const MappingListPage = () => {
  const api = WizardService.Mapping;
  const navigate = useNavigate();

  // State and constants
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [records, setRecords] = React.useState<Mapping[]>([]);
  const [selectedRecords, setSelectedRecords] = React.useState<number[]>([]);
  const [isCreateOpen, setCreateOpen] = React.useState<boolean>(false);

  const [pagination, setPagination] = React.useState<TablePaginationConfig>({
    current: searchParams.get("page") !== null ? +searchParams.get("page")! : 1,
    pageSize: searchParams.get("per") !== null ? +searchParams.get("per")! : 10,
    showSizeChanger: true,
  });

  const columns = [
    builder.id,
    {
      ...builder.text("Status", "status"),
      width: 160,
      render: (text: string) => <Tag color="blue">{text}</Tag>,
    },
    {
      ...builder.text("Display Name", "display", true),
      width: 220,
    },
    { ...builder.category("category"), width: 130 },
    builder.adType("Ad Type", "adTypes"),
    { ...builder.requirement("Requirement", "requirement"), width: 130 },
    {
      ...builder.text("Warning", "warning"),
      render: (text?: string) =>
        text ? (
          <Text type="warning">{text}</Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    builder.dropdown(
      [DropdownAction.VIEW, DropdownAction.COPY, DropdownAction.DELETE],
      (id, action) => {
        switch (action) {
          case DropdownAction.VIEW:
            return viewRecord(id);
          case DropdownAction.COPY:
            return copyRecord(id);
          case DropdownAction.DELETE:
            return removeRecord(id);
        }
      }
    ),
  ];

  // Handlers
  const viewRecord = (id: number) => {
    navigate("/" + AppRoutes.Wizard.Mapping.replace(":id", String(id)));
  };

  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => {
      setSelectedRecords(selectedRows.map((row) => row.key));
    },
  };

  const removeRecord = (id: number) => {
    setIsLoading(true);
    api
      .delete(id)
      .then((_) => fetch())
      .catch((error) => message.error(error.message))
      .finally(() => setIsLoading(false));
  };

  const removeSelectedRecords = () => {
    setIsLoading(true);
    Promise.all(selectedRecords.map((id) => api.delete(id)))
      .then((_) => fetch())
      .catch((error) => message.error(error.message))
      .finally(() => setIsLoading(false));
  };

  const createRecord = (record: Mapping) => {
    setIsLoading(true);
    api
      .create(record)
      .then((created) => {
        setCreateOpen(false);
        navigate(
          "/" + AppRoutes.Wizard.Mapping.replace(":id", String(created.id))
        );
      })
      .catch((error) => message.error(error.message))
      .finally(() => setIsLoading(false));
  };

  const copyRecord = (id: number) => {
    setIsLoading(true);
    api
      .copy(id)
      .then((copiedMapping) => {
        navigate(
          "/" +
            AppRoutes.Wizard.Mapping.replace(":id", String(copiedMapping.id))
        );
      })
      .catch((error) => message.error(error.message))
      .finally(() => setIsLoading(false));
  };

  // Calbacks
  const fetch = useCallback(() => {
    setSelectedRecords([]);
    setIsLoading(true);
    api
      .all(searchParams)
      .then((response) => {
        setRecords(response.items || []);
        setPagination((state) => ({
          ...state,
          current: response.metadata.page,
          pageSize: response.metadata.per,
          total: response.metadata.total,
        }));
      })
      .catch((error) => message.error(error.message))
      .finally(() => setIsLoading(false));
  }, [searchParams, api]);

  // Hooks
  useEffect(() => {
    fetch();
  }, [fetch]);

  return (
    <Layout>
      <Content>
        <PageHeader title="Mapping">
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined  />}
              onClick={(_) => setCreateOpen(true)}
            >
              Create
            </Button>
            <Popconfirm
              title="Delete Mappings"
              description="Are you sure to delete selected mappings?"
              onConfirm={removeSelectedRecords}
              okText="Yes"
              cancelText="No"
            >
              <Button
                danger
                icon={<DeleteOutlined  />}
                disabled={selectedRecords.length === 0}
              >
                Delete
              </Button>
            </Popconfirm>
          </Space>
        </PageHeader>
        <Row>
          <Col flex="100%">
            <Table
              scroll={{ x: true }}
              loading={isLoading}
              columns={columns}
              pagination={pagination}
              onRow={(record) => ({
                onClick: () => {
                  if (record.id !== undefined) viewRecord(record.id);
                },
              })}
              onChange={(pagination) => {
                setPagination(pagination);
                const params = new URLSearchParams(searchParams);

                pagination.current
                  ? params.set("page", pagination.current.toString())
                  : params.delete("page");
                pagination.pageSize
                  ? params.set("per", pagination.pageSize.toString())
                  : params.delete("per");

                setSearchParams(params);
              }}
              dataSource={records.map((record) => ({
                ...record,
                key: record.id,
              }))}
              rowSelection={rowSelection}
            />
          </Col>
        </Row>
      </Content>
      <Drawer
        title="Create a new Mapping"
        width={600}
        onClose={(_) => setCreateOpen(false)}
        open={isCreateOpen}
        styles={{
          body: {
            paddingBottom: 80,
          },
        }}
      >
        <MappingForm isLoading={isLoading} onFinish={createRecord} />
      </Drawer>
    </Layout>
  );
};
